import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport, type UIMessage } from 'ai'
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react'

interface TextUIPart {
  type: 'text'
  text: string
}

type MessagePart = TextUIPart | { type: string; [key: string]: unknown }

interface ChatMessageMetadata {
  createdAt?: string
  inputTokens?: number
  outputTokens?: number
  totalTokens?: number
  cachedInputTokens?: number
  costUsd?: number | null
  estimatedCostUsd?: number | null
  modelKey?: string | null
  finishReason?: string | null
  toolCallsCount?: number | null
  toolCalls?: any[]
}

interface LLMChatMessage extends UIMessage<ChatMessageMetadata> {
  content?: string
  toolCalls?: any[]
  costUsd?: number | null
  createdAt?: string | Date
}

interface UseLLMChatProps {
  chatId: number
  token: string
  firstMessage?: string
  historyMessages?: any[]
  onResponse?: (response: ArtificialIntelligenceResponseAnalytics) => void
}

interface ArtificialIntelligenceResponseAnalytics {
  usage?: ChatMessageMetadata | null
  finishReason?: string
  isAborted?: boolean
  isDisconnected?: boolean
  isError?: boolean
}

export default function useLLMChat({
  chatId,
  token,
  firstMessage,
  historyMessages,
  onResponse,
}: UseLLMChatProps) {
  const [input, setInput] = useState('')
  const isWaitingForFirstMessage = useRef<boolean>(true)

  const transport = useMemo(
    () =>
      new DefaultChatTransport<LLMChatMessage>({
        api: '/api/v1/llm-chat-message/create',
        headers: { Token: token },
        prepareSendMessagesRequest: options => {
          const { api, id, messages, headers, credentials, body } = options

          const lastMessage = (messages as LLMChatMessage[])[messages.length - 1]
          if (!lastMessage) {
            return {
              api,
              headers,
              credentials,
              body: body ?? {},
            }
          }

          const textParts = (lastMessage.parts ?? []).filter(
            (part: any) => part.type === 'text',
          ) as TextUIPart[]

          const textContent = textParts.map(part => part.text).join('')

          return {
            api,
            headers,
            credentials,
            body: {
              ...(body ?? {}),
              chatId,
              message: {
                id: lastMessage.id ?? `${id}-${messages.length}`,
                role: 'user',
                content: textContent,
                createdAt: new Date().toISOString(),
              },
            },
          }
        },
      }),
    [chatId, token],
  )

  /** Hook de chat do AI SDK (com streaming) */
  const chat = useChat<LLMChatMessage>({
    id: String(chatId),
    transport,
    onFinish({ message, finishReason, isAbort, isDisconnect, isError }) {
      onResponse?.({
        usage: message.metadata ?? null,
        finishReason,
        isAborted: isAbort,
        isDisconnected: isDisconnect,
        isError,
      })
    },
  })

  const { messages, setMessages, status, sendMessage } = chat

  const isLoading = status === 'submitted' || status === 'streaming'

  /* Atualiza as mensagens iniciais quando houver mudança de histórico */
  useEffect(() => {
    if (historyMessages && historyMessages.length > 0) {
      const mappedMessages: LLMChatMessage[] = historyMessages.map((historyMessage: any) => ({
        id: String(historyMessage.id),
        role: historyMessage.role,
        content: historyMessage.content,
        parts: historyMessage.content
          ? [
              {
                type: 'text',
                text: historyMessage.content,
              } as TextUIPart,
            ]
          : [],
        metadata: {
          createdAt: historyMessage.createdAt,
          costUsd: historyMessage.costUsd,
          modelKey: historyMessage.modelKey,
          totalTokens: historyMessage.tokenCount,
          toolCalls: historyMessage.toolCalls ?? [],
        },
        createdAt: historyMessage.createdAt,
        costUsd: historyMessage.costUsd,
        toolCalls: historyMessage.toolCalls ?? [],
      }))

      setMessages(mappedMessages)
      isWaitingForFirstMessage.current = true
    } else {
      setMessages([])
      isWaitingForFirstMessage.current = true
    }
  }, [historyMessages, chatId, setMessages])

  const handleInputChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(event.target.value)
  }, [])

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const text = input.trim()
      if (!text) return

      void sendMessage({ text })
      setInput('')
    },
    [input, sendMessage],
  )

  /* Envia a primeira mensagem automaticamente se for um chat novo */
  useEffect(() => {
    if (!firstMessage) return
    if (historyMessages && historyMessages.length > 0) return
    if (!isWaitingForFirstMessage.current) return
    if (isLoading) return

    isWaitingForFirstMessage.current = false
    void sendMessage({ text: firstMessage })
  }, [firstMessage, historyMessages, isLoading, sendMessage])

  /* Função para formatar custo salvo no banco (para mensagens históricas) */
  const formatSavedCost = useCallback((costUsd: number | null) => {
    if (!costUsd || costUsd <= 0) return null
    return `$${costUsd.toFixed(6)}`
  }, [])

  const sendQuickQuestion = useCallback(
    (text: string) => {
      const value = text.trim()
      if (!value) return
      if (isLoading) return
      void sendMessage({ text: value })
    },
    [sendMessage, isLoading],
  )

  return {
    ...chat,
    input,
    setInput,
    handleInputChange,
    handleSubmit,
    isLoading,
    reload: chat.regenerate,
    sendQuickQuestion,
    lastUsage: null,
    formatCostFromUsage: () => null,
    formatSavedCost,
  }
}

export type {
  TextUIPart,
  MessagePart,
  ChatMessageMetadata,
  ArtificialIntelligenceResponseAnalytics,
  LLMChatMessage,
}
